import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useJWTAuth } from "./context/JWTAuthContext";
import { JWTAuthProvider } from "./context/JWTAuthContext";
import { FamilyProvider } from "./context/FamilyContext";
import { TutorProvider } from "./context/TutorContext";
import { useTheme } from "./hooks/useTheme";
import { ThemeProvider } from "./context/ThemeContext";
import { PWAProvider } from "./context/PWAContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SimpleLoginPage from "./pages/auth/SimpleLoginPage";
import AuthCallback from "./pages/auth/AuthCallback";
import Layout from "./components/layout/Layout";
import AuthWrapper from "./pages/auth/AuthWrapper";
import AdminRoute from "./components/AdminRoute";
import AppErrorBoundary from "./components/AppErrorBoundary";
import RouteChunkBoundary from "./components/RouteChunkBoundary";
import SuperadminRoute from "./components/SuperadminRoute";
import TutorRoute from "./components/TutorRoute";
import PWAUpdatePrompt from "./components/PWAUpdatePrompt";
import PWAInstallGuide from "./components/PWAInstallGuide";
import {
  AUTH_TRANSITION_STORAGE_KEY,
  getProtectedRouteState,
  hasPendingAuthTransition,
} from "./utils/authStartup";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const OrdersList = lazy(() => import("./pages/OrdersList"));
const OrderDetailsPage = lazy(() => import("./pages/OrderDetailsPage"));
const CanteenWeekOrders = lazy(() => import("./pages/CanteenWeekOrders"));
const FinancePage = lazy(() => import("./pages/FinancePage"));
const NutritionDataPage = lazy(() => import("./pages/NutritionDataPage"));
const DishManagement = lazy(() => import("./pages/DishManagement"));
const NewMenu = lazy(() => import("./pages/NewMenu"));
const StudentBalancesPage = lazy(() => import("./pages/StudentBalancesPage"));
const Settings = lazy(() => import("./pages/Settings"));
const TutorListPage = lazy(() => import("./pages/TutorListPage"));
const TutorHubPage = lazy(() => import("./pages/TutorHubPage"));
const TutorClassOrdersPage = lazy(() => import("./pages/TutorClassOrdersPage"));
const TutorStudentOrders = lazy(() => import("./pages/TutorStudentOrders"));
const NewOrderPage = lazy(() => import("./pages/NewOrderPage-ENHANCED"));
const MenuHistoryPage = lazy(() => import("./pages/MenuHistoryPage"));
const MenuDetailPage = lazy(() => import("./pages/MenuDetailPage"));
const TutorManagementPage = lazy(() => import("./pages/admin/TutorManagementPage"));
const UserManagementPage = lazy(() => import("./pages/admin/UserManagementPage"));
const AdminOrderPlacementPage = lazy(
  () => import("./pages/admin/AdminOrderPlacementPage"),
);
const PushNotificationsManagement = lazy(
  () => import("./pages/admin/PushNotificationsManagement"),
);
const AdminAddPayment = lazy(() => import("./pages/admin/AdminAddPayment"));
const PaymentRequestsPage = lazy(() => import("./pages/PaymentRequestsPage"));
const PaymentsReportPage = lazy(() => import("./pages/PaymentsReportPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const ParentPaymentsHistory = lazy(
  () => import("./pages/ParentPaymentsHistory"),
);
const SupportChatPage = lazy(() => import("./pages/SupportChatPage"));
const MegaadminDashboardPage = lazy(
  () => import("./pages/MegaadminDashboardPage"),
);
const ErrorLogsPage = lazy(() => import("./pages/admin/ErrorLogsPage"));
const ActionLogPage = lazy(() => import("./pages/admin/ActionLogPage"));
const AuthDiagnosticsPage = lazy(
  () => import("./pages/admin/AuthDiagnosticsPage"),
);
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const UserAgreement = lazy(() => import("./pages/legal/UserAgreement"));
const PersonalDataConsent = lazy(
  () => import("./pages/legal/PersonalDataConsent"),
);

import MaintenanceStub, {
  MAINTENANCE_MODE,
  isMaintenanceBypassed,
} from "./components/MaintenanceStub";

function AppContent() {
  if (MAINTENANCE_MODE && !isMaintenanceBypassed()) {
    return <MaintenanceStub />;
  }
  const { isAuthenticated, loading, authResolved } = useJWTAuth();

  const renderLoadingScreen = () => (
    <AuthWrapper>
      <div className="full-screen-center">
        <div className="loading-spinner" />
        <div className="loading-text">Загрузка приложения...</div>
      </div>
    </AuthWrapper>
  );

  const renderProtectedElement = (element) => {
    const authTransitionPending = hasPendingAuthTransition(
      sessionStorage.getItem(AUTH_TRANSITION_STORAGE_KEY),
    );
    const routeState = getProtectedRouteState({
      authResolved,
      isAuthenticated,
      authTransitionPending,
    });

    if (routeState === "loading") {
      return renderLoadingScreen();
    }

    if (routeState === "redirect") {
      return <Navigate to="/login" replace />;
    }

    return element;
  };

  const renderLazyElement = (element) => (
    <RouteChunkBoundary>
      <Suspense fallback={renderLoadingScreen()}>{element}</Suspense>
    </RouteChunkBoundary>
  );

  // Don't block the landing page or auth callback on loading
  // This allows the PWA install prompt and hero section to show immediately
  if (
    loading &&
    !authResolved &&
    window.location.pathname !== "/" &&
    !window.location.pathname.startsWith("/auth/callback")
  ) {
    return renderLoadingScreen();
  }

  return (
    <AuthWrapper>
      <Routes>
        <Route
          path="/login"
          element={
            !authResolved ? (
              renderLoadingScreen()
            ) : !isAuthenticated ? (
              <SimpleLoginPage />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />

        <Route
          path="/simple-login"
          element={<Navigate to="/login" replace />}
        />

        <Route path="/privacy" element={renderLazyElement(<PrivacyPolicy />)} />
        <Route path="/terms" element={renderLazyElement(<UserAgreement />)} />
        <Route
          path="/consent"
          element={renderLazyElement(<PersonalDataConsent />)}
        />

        {/* Auth Callback - публичный route для Magic Link */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/dashboard"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <Dashboard />
            </Layout>,
          ))}
        />
        <Route
          path="/finance"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <FinancePage />
            </Layout>,
          ))}
        />
        <Route
          path="/profile/payments"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <ParentPaymentsHistory />
            </Layout>,
          ))}
        />

        <Route
          path="/orders/new"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <NewOrderPage />
            </Layout>,
          ))}
        />
        {/* /new-order -> редирект на /orders/new (обратная совместимость) */}
        <Route
          path="/new-order"
          element={<Navigate to="/orders/new" replace />}
        />
        <Route
          path="/new-order/:studentId"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <NewOrderPage />
            </Layout>,
          ))}
        />
        <Route
          path="/orders/details/:orderId"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <OrderDetailsPage />
            </Layout>,
          ))}
        />
        <Route
          path="/orders"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <OrdersList />
            </Layout>,
          ))}
        />
        <Route
          path="/orders/:studentId"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <OrdersList />
            </Layout>,
          ))}
        />
        <Route
          path="/order-details"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <OrderDetailsPage />
            </Layout>,
          ))}
        />
        <Route
          path="/settings"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <Settings />
            </Layout>,
          ))}
        />
        {/* Группа роутов для тьюторов */}
        <Route element={<TutorRoute />}>
          <Route
            path="/tutor"
            element={renderLazyElement(
              <Layout>
                <TutorHubPage />
              </Layout>
            )}
          />
          <Route
            path="/tutor-list"
            element={renderLazyElement(
              <Layout>
                <TutorListPage />
              </Layout>
            )}
          />
          <Route
            path="/tutor/class-orders"
            element={renderLazyElement(
              <Layout>
                <TutorClassOrdersPage />
              </Layout>
            )}
          />
          <Route
            path="/tutor/student-orders"
            element={renderLazyElement(
              <Layout>
                <TutorStudentOrders />
              </Layout>
            )}
          />
        </Route>

        {/* Группа роутов для администрирования */}
        <Route element={<AdminRoute />}>
          <Route
            path="/admin/menu-history"
            element={renderLazyElement(
              <Layout>
                <MenuHistoryPage />
              </Layout>
            )}
          />
          <Route
            path="/admin/menu-history/:menuId"
            element={renderLazyElement(
              <Layout>
                <MenuDetailPage />
              </Layout>
            )}
          />
          <Route
            path="/admin/new-menu"
            element={renderLazyElement(
              <Layout>
                <NewMenu />
              </Layout>
            )}
          />
          <Route
            path="/admin/tutors"
            element={renderLazyElement(
              <Layout>
                <TutorManagementPage />
              </Layout>
            )}
          />
          <Route
            path="/admin/nutrition-data"
            element={renderLazyElement(
              <Layout>
                <NutritionDataPage />
              </Layout>
            )}
          />
          <Route
            path="/admin/order-placement"
            element={renderLazyElement(
              <Layout>
                <AdminOrderPlacementPage />
              </Layout>
            )}
          />
          <Route
            path="/student-balances"
            element={renderLazyElement(
              <Layout>
                <StudentBalancesPage />
              </Layout>
            )}
          />
          <Route
            path="/admin/payments/new"
            element={renderLazyElement(
              <Layout>
                <AdminAddPayment />
              </Layout>
            )}
          />
          <Route
            path="/admin/action-log"
            element={renderLazyElement(
              <Layout>
                <ActionLogPage />
              </Layout>
            )}
          />
          <Route
            path="/megaadmin/payment-requests"
            element={renderLazyElement(
              <Layout>
                <PaymentRequestsPage />
              </Layout>
            )}
          />
          <Route
            path="/megaadmin/payments-report"
            element={renderLazyElement(
              <Layout>
                <PaymentsReportPage />
              </Layout>
            )}
          />
          <Route
            path="/canteen-week-orders"
            element={renderLazyElement(
              <Layout>
                <CanteenWeekOrders />
              </Layout>
            )}
          />
        </Route>

        {/* Группа роутов для супер администрирования */}
        <Route element={<SuperadminRoute />}>
          <Route
            path="/admin/user-management"
            element={renderLazyElement(
              <Layout>
                <UserManagementPage />
              </Layout>
            )}
          />
          <Route
            path="/admin/dish-management"
            element={renderLazyElement(
              <Layout>
                <DishManagement />
              </Layout>
            )}
          />
          {/* Редиректы для устаревших путей */}
          <Route
            path="/new-menu"
            element={<Navigate to="/admin/new-menu" replace />}
          />
          <Route
            path="/dish-management"
            element={<Navigate to="/admin/dish-management" replace />}
          />
          <Route
            path="/megaadmin/analytics"
            element={renderLazyElement(
              <Layout>
                <MegaadminDashboardPage />
              </Layout>
            )}
          />
          <Route
            path="/admin/push-notifications"
            element={renderLazyElement(
              <Layout>
                <PushNotificationsManagement />
              </Layout>
            )}
          />
          <Route
            path="/megaadmin/error-logs"
            element={renderLazyElement(
              <Layout>
                <ErrorLogsPage />
              </Layout>
            )}
          />
          <Route
            path="/megaadmin/auth-diagnostics"
            element={renderLazyElement(
              <Layout>
                <AuthDiagnosticsPage />
              </Layout>
            )}
          />
        </Route>

        {/* Уведомления - для всех авторизованных пользователей */}
        <Route
          path="/notifications"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <NotificationsPage />
            </Layout>,
          ))}
        />

        {/* Меню / данные по питанию — доступно всем авторизованным */}
        <Route
          path="/nutrition-data"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <NutritionDataPage />
            </Layout>,
          ))}
        />

        <Route
          path="/support"
          element={renderProtectedElement(renderLazyElement(
            <Layout>
              <SupportChatPage />
            </Layout>,
          ))}
        />

        <Route
          path="/"
          element={
            !authResolved ? (
              renderLoadingScreen()
            ) : isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="*"
          element={
            !authResolved ? (
              renderLoadingScreen()
            ) : (
              <Navigate
                to={isAuthenticated ? "/dashboard" : "/login"}
                replace
              />
            )
          }
        />
      </Routes>
    </AuthWrapper>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppErrorBoundary>
        <PWAProvider>
          <PWAUpdatePrompt />
          <PWAInstallGuide />
          <JWTAuthProvider>
            <FamilyProvider>
              <TutorProvider>
                <AppContent />
                <ThemeAwareToast />
              </TutorProvider>
            </FamilyProvider>
          </JWTAuthProvider>
        </PWAProvider>
      </AppErrorBoundary>
    </ThemeProvider>
  );
}

const ThemeAwareToast = () => {
  const { theme } = useTheme();
  return (
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={theme === "dark" ? "dark" : "light"}
    />
  );
};

export default App;
